"use client";

import { useEffect, useRef, useState } from "react";
import { Users, BookOpen, Award, Server } from "lucide-react";

const stats = [
  { icon: Users,    numeric: 500, suffix: "+", label: "Practitioners trained" },
  { icon: BookOpen, numeric: 95,  suffix: "+", label: "Hands-on labs" },
  { icon: Award,    numeric: 3,   suffix: "",  label: "Practical certifications" },
  { icon: Server,   numeric: null, suffix: "", text: "Real", label: "Cloud attack environments" },
];

function useCountUp(target: number, duration: number, active: boolean) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    let raf: number;
    let start: number | null = null;

    const tick = (ts: number) => {
      if (start === null) start = ts;
      const elapsed = ts - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, active]);

  return value;
}

function AnimatedNumber({
  target,
  suffix,
  active,
}: {
  target: number;
  suffix: string;
  active: boolean;
}) {
  const count = useCountUp(target, 1400, active);
  return <>{count}{suffix}</>;
}

export function TrustBar() {
  const ref = useRef<HTMLElement>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setActive(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="border-y border-cb-border bg-cb-surface">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 md:divide-x divide-cb-border">
          {stats.map(({ icon: Icon, numeric, suffix, text, label }) => (
            <div key={label} className="flex items-center gap-3 md:px-8 first:md:pl-0 last:md:pr-0">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-cb-violet/10 border border-cb-violet/20">
                <Icon className="h-5 w-5 text-cb-violet-text" />
              </div>
              <div>
                <p className="text-3xl font-bold text-cb-text tabular-nums">
                  {numeric !== null ? (
                    <AnimatedNumber target={numeric} suffix={suffix} active={active} />
                  ) : (
                    text
                  )}
                </p>
                <p className="text-xs text-cb-muted">{label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
